import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle2, FileText, Loader2, Sparkles, ShieldCheck, BarChart3, Globe2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRY_PRICES = {
  UK: { price: '£49',   amount: 49,   currency: 'GBP', flag: '🇬🇧', label: 'United Kingdom' },
  IN: { price: '₹4,900', amount: 4900, currency: 'INR', flag: '🇮🇳', label: 'India' },
  CA: { price: 'C$79',  amount: 79,   currency: 'CAD', flag: '🇨🇦', label: 'Canada' },
};

export default function FreeValuationPage() {
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';
  const initCountry = (params.get('country') || 'UK').toUpperCase();
  const initCity = params.get('city') || '';
  const [country, setCountry] = useState(['UK', 'IN', 'CA'].includes(initCountry) ? initCountry : 'UK');
  const [form, setForm] = useState({ name: '', email: '', city: initCity, postcode: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Pulse Investment Report — AI-powered property analysis | Shopperden';
  }, []);

  const cfg = COUNTRY_PRICES[country];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.city) {
      toast.error('Email and city are required');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pulse-report/checkout`, {
        ...form,
        country,
        ref: ref || undefined,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0C', color: '#FAFAFA' }} data-testid="free-valuation-page">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <Link to="/" className="text-sm tracking-widest text-white/60 hover:text-[#D4AF37]">← SHOPPERDEN</Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8 items-start">
          {/* Left: pitch */}
          <div>
            <p className="text-[11px] tracking-[0.3em] text-[#D4AF37]">PULSE INVESTMENT REPORT</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
              The 5-page property report<br/>
              <span className="text-[#D4AF37]">your agent won't give you.</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              Independent AI-powered analysis of any UK · India · Canada market. Median pricing,
              sample size, comparable listings, and a 12-month outlook — sourced from our 1,000+
              listings/day harvested intelligence index.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              {[
                { icon: <BarChart3 className="h-5 w-5" />, text: 'Median + min + max + average price across 30 live comparables' },
                { icon: <Sparkles className="h-5 w-5" />, text: '250-word AI-generated commentary with NRI/foreign-buyer context' },
                { icon: <FileText className="h-5 w-5" />, text: 'Top 5 engagement comparables ranked by buyer-click data' },
                { icon: <ShieldCheck className="h-5 w-5" />, text: 'Independent — not from a selling agent. No sales pitch inside.' },
                { icon: <Globe2 className="h-5 w-5" />, text: 'UK · India · Canada coverage — including non-portal listings' },
                { icon: <CheckCircle2 className="h-5 w-5" />, text: 'PDF emailed within 60 seconds of payment' },
              ].map((b, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-0.5">{b.icon}</span>
                  <span className="text-white/80 text-[14px] leading-snug">{b.text}</span>
                </div>
              ))}
            </div>

            <p className="mt-12 text-xs text-white/40 max-w-xl">
              Shopperden Ltd is an introduction service — not an estate agent or financial advisor.
              Reports are based on publicly available listings and AI commentary; readers should
              independently verify price-sensitive decisions.
            </p>
          </div>

          {/* Right: checkout */}
          <div className="lg:sticky lg:top-12">
            <div style={{ background: '#121215', border: '1px solid #2E2E34', padding: '32px' }}>
              <p className="text-[11px] tracking-[0.3em] text-[#D4AF37]">ORDER YOUR REPORT</p>

              {/* Country selector */}
              <div className="flex gap-2 mt-3">
                {Object.entries(COUNTRY_PRICES).map(([k, c]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setCountry(k)}
                    className="text-xs px-3 py-2 flex-1"
                    style={{
                      background: country === k ? '#D4AF37' : '#0A0A0C',
                      color: country === k ? '#0A0A0C' : '#FAFAFA',
                      border: '1px solid #2E2E34',
                      fontWeight: country === k ? 700 : 400,
                    }}
                    data-testid={`pulse-country-${k}`}
                  >
                    {c.flag} {c.label}
                  </button>
                ))}
              </div>

              <div className="flex items-baseline gap-2 mt-5">
                <span className="font-['Playfair_Display'] text-5xl text-white">{cfg.price}</span>
                <span className="text-white/60 text-sm">one-time</span>
              </div>
              <p className="text-white/50 text-xs mt-1">PDF delivered within 60s · {cfg.currency} via Stripe</p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="pulse-form">
                <input
                  required type="text" placeholder="Your name (optional)"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="pulse-form-name"
                />
                <input
                  required type="email" placeholder="Email (PDF delivered here)"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="pulse-form-email"
                />
                <input
                  required type="text" placeholder="City to analyse (e.g. Mohali, Hyderabad, London)"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="pulse-form-city"
                />
                <input
                  type="text" placeholder="Postcode / area (optional)"
                  value={form.postcode}
                  onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="pulse-form-postcode"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 text-sm font-bold tracking-widest mt-2 flex items-center justify-center gap-2"
                  style={{ background: '#D4AF37', color: '#0A0A0C' }}
                  data-testid="pulse-form-submit"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  ORDER REPORT — {cfg.price}
                </button>
              </form>

              <div className="mt-6 pt-6 space-y-2" style={{ borderTop: '1px solid #2E2E34' }}>
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span>Secure checkout by Stripe</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span>PDF auto-delivered to your email</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span>Free regeneration if data is sparse</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
